import { Component, createRef } from "react";
import { ButtonGroup, Col, Container, Form, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setPlayerConfigAC, switchStateAC } from "../../actions/game";
import Edit from "../../assets/icons/edit.svg";
import EmojiPeople from "../../assets/icons/emoji_people.svg";
import Image from "../../assets/icons/image.svg";
import "../../assets/icons/material-ui-icon.css";
import Power from "../../assets/icons/power.svg";
import PresentToAll from "../../assets/icons/present_to_all.svg";
import PlayCircleOutline from "../../assets/icons/play_circle_outline.svg";
import VpnKey from "../../assets/icons/vpn_key.svg";
import { applyThemeVars } from "../../branding/theme";
import BrandLogo from "../../assets/logo";
import CenterBox from "../../components/CenterBox";
import HostLoginModal from "../../components/HostLoginModal";
import IconButton from "../../components/IconButton";
import { server } from "../../connection/config";
import { hasHostSession, getHostRoomCode, hasValidHostGrant, getHostRole, hasHostAccess, hasAdminAccess, ROLES } from "../../connection/host-auth";
import { getSession, hasActiveSession, clearSession } from "../../connection/reconnect";
import { installOnBeforeUnloadListener, isValidRoomCode, onExitGame } from "../../utilities";
import { processAvatarImage, getInitials } from "../../utilities/avatar";
import "./main.css";

class Main extends Component {
    constructor(props) {
        super(props);
        this.state = {
            roomCode: "",
            playerName: "",
            avatarThumb: null,
            avatarProcessing: false,
            showLoginModal: false,
            hostAuthEnabled: null,  // null = loading, true/false = loaded
            adminAuthEnabled: null, // null = loading, true/false = loaded
            hasHostAccess: hasHostAccess() || hasHostSession(),
            currentRole: getHostRole()  // "admin", "host", or null
        };
        this.userNameReference = createRef();
        this.roomCodeReference = createRef();
        this.avatarInputRef = createRef();
        this.onInputFieldKeyEvent = this.onInputFieldKeyEvent.bind(this);
        this.reconnect = this.reconnect.bind(this);
        this.onNameChanged = this.onNameChanged.bind(this);
        this.startGame = this.startGame.bind(this);
        this.changeRoomCode = this.changeRoomCode.bind(this);
        this.openLoginModal = this.openLoginModal.bind(this);
        this.closeLoginModal = this.closeLoginModal.bind(this);
        this.onLoginSuccess = this.onLoginSuccess.bind(this);
        this.leaveGame = this.leaveGame.bind(this);
        this.onAvatarSelect = this.onAvatarSelect.bind(this);
        this.removeAvatar = this.removeAvatar.bind(this);
        this.triggerAvatarInput = this.triggerAvatarInput.bind(this);
    }

    componentDidMount() {
        onExitGame();
        this.props.switchState("");
        this.props.setPlayerConfig("", "", false);
        if (isValidRoomCode(this.props.roomCode)) {
            this.setState({ roomCode: `${this.props.roomCode}` }, () => this.focusOnInput());
            // Fetch and apply room theme immediately (before user clicks Join)
            this.fetchRoomTheme(this.props.roomCode);
        } else {
            this.focusOnInput();
            // Check if host auth is enabled (only on main page without room code)
            this.checkHostAuthStatus();
        }
    }

    /**
     * Check if host/admin authentication is enabled on the server.
     */
    async checkHostAuthStatus() {
        try {
            const response = await fetch(`${server}/api/host/auth/status`);
            if (response.ok) {
                const { enabled, hostEnabled, adminEnabled } = await response.json();
                this.setState({
                    hostAuthEnabled: enabled,  // backwards compat
                    adminAuthEnabled: adminEnabled
                });
            }
        } catch (e) {
            console.warn("Could not check host auth status:", e);
            // Assume disabled on error (allows local dev without backend)
            this.setState({ hostAuthEnabled: false, adminAuthEnabled: false });
        }
    }

    openLoginModal() {
        this.setState({ showLoginModal: true });
    }

    closeLoginModal() {
        this.setState({ showLoginModal: false });
    }

    onLoginSuccess(role) {
        this.setState({
            showLoginModal: false,
            hasHostAccess: true,
            currentRole: role
        });
    }

    /**
     * Fetch room theme from API and apply it immediately.
     * This provides branded experience as soon as QR code link is opened.
     */
    async fetchRoomTheme(roomCode) {
        try {
            const response = await fetch(`${server}/api/rooms/${roomCode}/theme`);
            if (!response.ok) return; // Room not found or no theme, silently ignore

            const data = await response.json();
            if (data.theme) {
                // Save to localStorage and apply CSS variables
                localStorage.setItem("expo_theme", JSON.stringify(data.theme));
                applyThemeVars(data.theme);
            }
        } catch (e) {
            // Network error or other failure - silently ignore
            console.warn("Could not fetch room theme:", e);
        }
    }

    onNameChanged(event) {
        this.setState({ playerName: event.target.value });
    }

    focusOnInput() {
        if (!this.state.playerName) {
            this.userNameReference.current.focus();
        } else if (!this.state.roomCode) {
            this.roomCodeReference.current.focus();
        }
    }

    onInputFieldKeyEvent(event) {
        installOnBeforeUnloadListener();
        if (event.key === "Enter" && isValidRoomCode(this.state.roomCode) && this.state.playerName.trim()) {
            this.startGame();
        }
    }

    changeRoomCode(event) {
        this.setState({ roomCode: event.target.value });
    }

    startGame() {
        if (this.state.roomCode !== "") {
            installOnBeforeUnloadListener();
            this.props.setPlayerConfig(
                this.state.roomCode,
                this.state.playerName,
                false,
                this.state.avatarThumb
            );
            this.props.navigate("/player");
        }
    }

    reconnect() {
        const session = getSession();
        if (session) {
            installOnBeforeUnloadListener();
            this.props.setPlayerConfig(session.roomCode, session.nickname, true);
            this.props.navigate("/player");
        }
    }

    leaveGame() {
        clearSession();
        // Force re-render to hide the rejoin button
        this.forceUpdate();
    }

    triggerAvatarInput() {
        if (this.avatarInputRef.current) {
            this.avatarInputRef.current.click();
        }
    }

    async onAvatarSelect(event) {
        const file = event.target.files?.[0];
        if (!file) return;

        this.setState({ avatarProcessing: true });

        try {
            const avatarThumb = await processAvatarImage(file);
            this.setState({ avatarThumb, avatarProcessing: false });
        } catch (error) {
            console.warn("Failed to process avatar:", error);
            this.setState({ avatarProcessing: false });
        }

        // Reset the input so the same file can be selected again
        event.target.value = "";
    }

    removeAvatar() {
        this.setState({ avatarThumb: null });
    }

    renderAvatarPicker() {
        const { avatarThumb, avatarProcessing, playerName } = this.state;

        return (
            <div className="avatar-picker">
                <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    ref={this.avatarInputRef}
                    onChange={this.onAvatarSelect}
                    className="avatar-picker-input"
                    aria-label="Select photo"
                />
                <div
                    className="avatar-picker-preview"
                    onClick={this.triggerAvatarInput}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && this.triggerAvatarInput()}
                    aria-label={avatarThumb ? "Change photo" : "Add photo"}
                >
                    {avatarProcessing ? (
                        <span className="avatar-picker-initials">...</span>
                    ) : avatarThumb ? (
                        <img src={avatarThumb} alt="Your avatar" />
                    ) : (
                        <span className="avatar-picker-initials">
                            {getInitials(playerName)}
                        </span>
                    )}
                </div>
                <div className="avatar-picker-label">
                    {avatarThumb ? "Tap to change" : "Add photo (optional)"}
                </div>
                {avatarThumb && (
                    <button
                        type="button"
                        className="avatar-picker-remove"
                        onClick={this.removeAvatar}
                    >
                        Remove photo
                    </button>
                )}
            </div>
        );
    }

    /**
     * Render admin controls based on auth state and role.
     * - No access: show "Host tools" button (opens login modal)
     * - Host role: show Quiz editor + Host a quiz
     * - Admin role: show Quiz editor + Host a quiz + Theme editor
     */
    renderAdminControls() {
        const { roomCode } = this.props;
        const { hostAuthEnabled, hasHostAccess, currentRole } = this.state;

        // Players arriving via QR code (roomCode in URL): only show if they have host session
        if (roomCode && !hasHostSession()) {
            return null;
        }

        // If still loading auth status, show nothing
        if (hostAuthEnabled === null && !roomCode) {
            return null;
        }

        // If auth is enabled and user doesn't have access yet, show "Host tools" button
        if (hostAuthEnabled && !hasHostAccess) {
            return (
                <ButtonGroup className="main-footer-btn">
                    <IconButton
                        icon={VpnKey}
                        label="Host tools"
                        buttonClassName="qm-fixed-bottom qm-fixed-left"
                        onClick={this.openLoginModal}
                    />
                </ButtonGroup>
            );
        }

        // Determine if user has admin access (role=admin)
        const isAdmin = currentRole === ROLES.ADMIN;

        // User has access (or auth disabled) - show controls based on role
        return (
            <ButtonGroup className="main-footer-btn">
                <IconButton
                    link="/editor"
                    icon={Edit}
                    label="Quiz editor"
                    buttonClassName="qm-fixed-bottom qm-fixed-left"
                />
                <span style={{ padding: "0.25rem" }}>{" "}</span>
                {hasHostSession() ? (
                    <IconButton
                        link="/host"
                        icon={PlayCircleOutline}
                        variant="success"
                        label={`Resume (${getHostRoomCode()})`}
                        buttonClassName="qm-fixed-bottom qm-fixed-left"
                    />
                ) : (
                    <IconButton
                        link="/host"
                        icon={PresentToAll}
                        label="Host a quiz"
                        buttonClassName="qm-fixed-bottom qm-fixed-left"
                    />
                )}
                {/* Theme editor only visible to admins */}
                {isAdmin && (
                    <>
                        <span style={{ padding: "0.25rem" }}>{" "}</span>
                        <IconButton
                            link="/theme"
                            icon={Image}
                            label="Theme editor"
                            buttonClassName="qm-fixed-bottom qm-fixed-left"
                        />
                    </>
                )}
            </ButtonGroup>
        );
    }

    render() {
        return (
            <CenterBox showGitHubLink>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <BrandLogo className="main-logo-text equal-width" alt="Expo Attractions" />
                            <form>
                                {hasActiveSession() && (
                                    <div>
                                        <br />
                                        <IconButton
                                            variant="warning"
                                            buttonClassName="reconnect-btn equal-width"
                                            buttonStyle={{ width: "100%", maxWidth: "18rem" }}
                                            icon={Power}
                                            label="Rejoin previous game"
                                            onClick={this.reconnect}
                                        />
                                        <br />
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm text-muted"
                                            onClick={this.leaveGame}
                                            style={{ marginTop: "0.25rem" }}
                                        >
                                            Leave quiz
                                        </button>
                                    </div>
                                )}
                                <Form.Control
                                    type="text"
                                    value={this.state.playerName}
                                    onChange={this.onNameChanged}
                                    placeholder="Name"
                                    ref={this.userNameReference}
                                    onKeyPress={this.onInputFieldKeyEvent}
                                    maxLength="40"
                                    className="main-input-field equal-width"
                                />
                                <Form.Control
                                    type="number"
                                    value={this.state.roomCode}
                                    onChange={this.changeRoomCode}
                                    ref={this.roomCodeReference}
                                    onKeyPress={this.onInputFieldKeyEvent}
                                    placeholder="6-digit access code"
                                    className="main-input-field equal-width"
                                />
                                {this.renderAvatarPicker()}
                                <IconButton
                                    disabled={!isValidRoomCode(this.state.roomCode) || !this.state.playerName.trim() || this.state.avatarProcessing}
                                    icon={EmojiPeople}
                                    variant="warning"
                                    btnClassName="btn-brand-primary"
                                    label="Join"
                                    buttonClassName="equal-width"
                                    buttonStyle={{ width: "100%", maxWidth: "18rem" }}
                                    onClick={this.startGame}
                                />
                            </form>

                            {/* Admin controls logic:
                                - Players via QR (roomCode in URL): hide everything unless they have a host session
                                - Main page (no roomCode):
                                  - If auth disabled OR user has access: show full admin buttons
                                  - If auth enabled AND no access: show "Host tools" button to open login modal
                            */}
                            {this.renderAdminControls()}

                            <HostLoginModal
                                show={this.state.showLoginModal}
                                onClose={this.closeLoginModal}
                                onSuccess={this.onLoginSuccess}
                            />
                        </Col>
                    </Row>
                </Container>
            </CenterBox>
        );
    }
}

const mapStateToProps = state => {
    return {
        game: state.game,
    };
};

const mapDispatchToProps = dispatch => ({
    switchState: (...args) => dispatch(switchStateAC(...args)),
    setPlayerConfig: (...args) => dispatch(setPlayerConfigAC(...args)),
});

const ConnectedMain = connect(mapStateToProps, mapDispatchToProps)(Main);

const ConnectedMainWithNavigate = props => (<ConnectedMain {...props} navigate={useNavigate()} />);

export default ConnectedMainWithNavigate;
